// Note: datadogRum instance will be injected from the plugin
import { createLogger } from '~/utils/logger';

const logger = createLogger('performance-metrics');

export interface PerformanceMetrics {
    fps: {
        current: number;
        average: number;
        history: number[];
    };
    frameTiming: {
        current: number;
        average: number;
        min: number;
        max: number;
        jankCount: number;
        jankRatio: number;
    };
}

export interface PerformanceThresholds {
    FPS_WARNING: number;
    FPS_CRITICAL: number;
    FRAME_TIME_WARNING: number;
    FRAME_TIME_CRITICAL: number;
    JANK_RATIO_WARNING: number;
    JANK_RATIO_CRITICAL: number;
}

export interface PerformanceReport {
    timestamp: number;
    fps: {
        current: number;
        average: number;
        min: number;
        max: number;
    };
    frameTiming: {
        current: number;
        average: number;
        min: number;
        max: number;
        jankCount: number;
        jankRatio: number;
    };
    indicators: {
        isLowFps: boolean;
        isCriticalFps: boolean;
        isSlowFrames: boolean;
        isCriticalFrames: boolean;
        hasJank: boolean;
        hasCriticalJank: boolean;
    };
}

/**
 * Performance metrics service for WebGL/Three.js applications
 * Handles collection, analysis, and reporting of performance metrics to Datadog
 */
export class PerformanceMetricsService {
    private metricsCollector: any = null;
    private reportingInterval: NodeJS.Timeout | null = null;
    private isInitialized = false;
    private datadogRum: any;

    // Configuration
    private readonly REPORTING_INTERVAL = 10000; // Report every 10 seconds

    public readonly thresholds: PerformanceThresholds = {
        FPS_WARNING: 30,
        FPS_CRITICAL: 20,
        FRAME_TIME_WARNING: 33, // 30fps
        FRAME_TIME_CRITICAL: 50, // 20fps
        JANK_RATIO_WARNING: 0.1,
        JANK_RATIO_CRITICAL: 0.2,
    };

    /**
     * Initialize performance metrics collection
     * @param performanceMetrics - Performance metrics instance from usePerformanceMetrics
     * @param datadogRum - Initialized Datadog RUM instance from plugin
     */
    public initialize(performanceMetrics: any, datadogRum: any): void {
        if (this.isInitialized) return;

        this.metricsCollector = performanceMetrics;
        this.datadogRum = datadogRum;
        this.isInitialized = true;

        // Start periodic reporting
        this.reportingInterval = setInterval(() => {
            this.reportMetrics();
        }, this.REPORTING_INTERVAL);
    }

    /**
     * Stop performance metrics collection
     */
    public stop(): void {
        if (this.reportingInterval) {
            clearInterval(this.reportingInterval);
            this.reportingInterval = null;
        }

        this.isInitialized = false;
    }

    /**
     * Send immediate performance report (for debugging)
     */
    public sendImmediateReport(): void {
        if (this.metricsCollector) {
            this.reportMetrics();
        }
    }

    /**
     * Update reporting interval
     * @param interval - New interval in milliseconds
     */
    public updateReportingInterval(interval: number): void {
        if (this.reportingInterval) {
            clearInterval(this.reportingInterval);
        }

        this.reportingInterval = setInterval(() => {
            this.reportMetrics();
        }, interval);
    }

    /**
     * Report performance metrics to Datadog
     */
    private reportMetrics(): void {
        if (!this.metricsCollector) return;

        try {
            const metrics = this.metricsCollector.getMetricsSnapshot();
            const performanceReport = this.createPerformanceReport(metrics);

            // Send to Datadog RUM as custom action
            this.datadogRum.addAction('gl_performance_metrics', performanceReport);

            // Send individual metrics for easier querying
            this.sendIndividualMetrics(performanceReport);

            // Check for performance issues and log warnings
            this.checkPerformanceThresholds(performanceReport);
        } catch (error) {
            logger.error('Error reporting performance metrics', error);
        }
    }

    /**
     * Create comprehensive performance report
     */
    private createPerformanceReport(metrics: PerformanceMetrics): PerformanceReport {
        return {
            timestamp: Date.now(),
            fps: {
                current: metrics.fps.current,
                average: metrics.fps.average,
                min: Math.min(...metrics.fps.history),
                max: Math.max(...metrics.fps.history),
            },
            frameTiming: {
                current: metrics.frameTiming.current,
                average: metrics.frameTiming.average,
                min: metrics.frameTiming.min,
                max: metrics.frameTiming.max,
                jankCount: metrics.frameTiming.jankCount,
                jankRatio: metrics.frameTiming.jankRatio,
            },
            indicators: {
                isLowFps: metrics.fps.average < this.thresholds.FPS_WARNING,
                isCriticalFps: metrics.fps.average < this.thresholds.FPS_CRITICAL,
                isSlowFrames: metrics.frameTiming.average > this.thresholds.FRAME_TIME_WARNING,
                isCriticalFrames: metrics.frameTiming.average > this.thresholds.FRAME_TIME_CRITICAL,
                hasJank: metrics.frameTiming.jankRatio > this.thresholds.JANK_RATIO_WARNING,
                hasCriticalJank:
                    metrics.frameTiming.jankRatio > this.thresholds.JANK_RATIO_CRITICAL,
            },
        };
    }

    /**
     * Send individual metrics for easier Datadog querying
     */
    private sendIndividualMetrics(report: PerformanceReport): void {
        // FPS metrics
        this.datadogRum.addAction('gl_fps', {
            current: report.fps.current,
            average: report.fps.average,
            min: report.fps.min,
            max: report.fps.max,
        });

        // Frame timing metrics
        this.datadogRum.addAction('gl_frame_timing', {
            current: report.frameTiming.current,
            average: report.frameTiming.average,
            min: report.frameTiming.min,
            max: report.frameTiming.max,
            jankRatio: report.frameTiming.jankRatio,
        });
    }

    /**
     * Check performance thresholds and log warnings/errors
     */
    private checkPerformanceThresholds(report: PerformanceReport): void {
        const { indicators } = report;

        // Critical performance issues
        if (indicators.isCriticalFps) {
            this.datadogRum.addError(
                new Error(`Critical FPS: ${report.fps.average.toFixed(1)} FPS`)
            );
        }

        if (indicators.isCriticalFrames) {
            this.datadogRum.addError(
                new Error(`Critical frame time: ${report.frameTiming.average.toFixed(1)}ms`)
            );
        }

        if (indicators.hasCriticalJank) {
            this.datadogRum.addError(
                new Error(
                    `Critical jank ratio: ${(report.frameTiming.jankRatio * 100).toFixed(1)}%`
                )
            );
        }

        // Warning level issues
        if (indicators.isLowFps && !indicators.isCriticalFps) {
            logger.warnOnce('Low FPS detected', {
                event: 'performance.threshold_warning',
                onceKey: 'performance.threshold_warning:fps',
                metric: 'fps',
                value: Number(report.fps.average.toFixed(1)),
                unit: 'fps',
                threshold: this.thresholds.FPS_WARNING,
            });
        }

        if (indicators.isSlowFrames && !indicators.isCriticalFrames) {
            logger.warnOnce('Slow frame time detected', {
                event: 'performance.threshold_warning',
                onceKey: 'performance.threshold_warning:frame_time',
                metric: 'frame_time',
                value: Number(report.frameTiming.average.toFixed(1)),
                unit: 'ms',
                threshold: this.thresholds.FRAME_TIME_WARNING,
            });
        }

        if (indicators.hasJank && !indicators.hasCriticalJank) {
            logger.warnOnce('Frame jank detected', {
                event: 'performance.threshold_warning',
                onceKey: 'performance.threshold_warning:jank_ratio',
                metric: 'jank_ratio',
                value: Number((report.frameTiming.jankRatio * 100).toFixed(1)),
                unit: 'percent',
                threshold: this.thresholds.JANK_RATIO_WARNING * 100,
            });
        }
    }

    /**
     * Get current thresholds
     */
    public getThresholds(): PerformanceThresholds {
        return this.thresholds;
    }

    /**
     * Check if service is initialized
     */
    public get isActive(): boolean {
        return this.isInitialized;
    }
}
